<!-- @format -->

<template>
	<div class="core-file-upload card">
		<div
			v-if="filePath && filePath.length > 0"
			class="flex justify-between"
		>
			<Button
				class="core-file-upload__download-button"
				icon="pi pi-download"
				:label="$t('§ui_button.download')"
				@click="$emit('download')"
			/>
			<Button
				class="core-file-upload__delete-button"
				icon="pi pi-trash"
				:label="$t('§ui_button.delete')"
				@click="$emit('delete')"
			/>
		</div>
		<FileUpload
			v-else
			mode="basic"
			ref="fileUploadComponent"
			:auto="auto"
			:accept="accept"
			:chooseLabel="chooseLabel"
			:disabled="isDisabled"
			:maxFileSize="10000000"
			:multiple="false"
			:name="name"
			:showCancelButton="false"
			:showUploadButton="false"
			:url="uploadUrl"
			@before-send="onBeforeSend"
			@upload="onAdvancedUpload($event)"
			@error="onUploadError($event)"
			@select="onSelect($event)"
		>
		</FileUpload>
	</div>
</template>

<script>
import FileUpload from 'primevue/fileupload'
import Button from 'primevue/button'

export default {
	name: 'CoreFileUpload',

	components: {
		FileUpload,
		Button,
	},
	props: {
		name: {
			type: String,
			default: null,
		},

		uploadUrl: {
			type: String,
			required: true,
		},

		filePath: {
			type: String,
			default: '',
		},

		accept: {
			type: String,
			default: 'image/*,.pdf,.xlsx,.xls',
		},

		disabled: {
			type: Boolean,
			default: false,
		},

		auto: {
			type: Boolean,
			default: false,
		},

		metadata: {
			type: Object,
			default: () => ({}),
		},

		acceptJson: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			uploading: false,
		}
	},

	computed: {
		chooseLabel() {
			return this.$t('§ui_button.browse_file')
		},

		isDisabled() {
			return this.uploading || this.disabled
		},
	},

	methods: {
		onBeforeSend(event) {
			this.uploading = true
			try {
				const csrfToken = document
					.querySelector('meta[name="csrf-token"]')
					.getAttribute('content')
				event.xhr.setRequestHeader('X-CSRF-TOKEN', csrfToken)

				if (this.acceptJson) {
					event.xhr.setRequestHeader('Accept', 'application/json')
				}

				if (this.metadata && Object.keys(this.metadata).length > 0) {
					Object.entries(this.metadata).forEach(([key, value]) => {
						// Add each metadata field to the FormData
						if (value !== null && value !== undefined) {
							event.formData.append(key, value)
						}
					})
				}
			} catch (e) {
				this.$toast.add({
					severity: 'error',
					summary: 'Error',
					detail: e.message,
					life: 1000,
				})
				this.uploading = false
			}
		},

		onAdvancedUpload(e) {
			this.uploading = false
			this.$emit('uploaded', e.xhr)
			this.$toast.add({
				severity: 'success',
				summary: 'Success',
				detail: 'File Uploaded',
				life: 1000,
			})
		},

		onUploadError(e) {
			this.uploading = false
			this.$toast.add({
				severity: 'error',
				summary: 'Error',
				detail: this.uploadErrorDetail(e?.xhr),
				life: 3000,
			})
			this.$emit('upload-error', e?.xhr)
		},

		uploadErrorDetail(xhr) {
			let message = null
			try {
				const body = JSON.parse(xhr?.response)
				const firstError = Object.values(body?.errors ?? {})[0]
				message = (Array.isArray(firstError) ? firstError[0] : firstError) ?? body?.message
			} catch (e) {
				message = null
			}
			if (typeof message !== 'string' || message.length === 0) {
				return 'Upload failed'
			}
			return message.startsWith('§') ? this.$t(message) : message
		},

		upload() {
			if (this.$refs.fileUploadComponent) {
				this.$refs.fileUploadComponent.upload()
			}
		},

		async uploadMetadataOnly() {
			this.uploading = true

			try {
				const xhr = new XMLHttpRequest()
				const formData = new FormData()

				if (this.metadata && Object.keys(this.metadata).length > 0) {
					Object.entries(this.metadata).forEach(([key, value]) => {
						if (value !== null && value !== undefined) {
							formData.append(key, value)
						}
					})
				}

				return new Promise((resolve, reject) => {
					xhr.onreadystatechange = () => {
						if (xhr.readyState === 4) {
							this.uploading = false

							if (xhr.status >= 200 && xhr.status < 300) {
								this.$emit('uploaded', { xhr })
								this.$toast.add({
									severity: 'success',
									summary: 'Success',
									detail: 'Updated Successfully',
									life: 1000,
								})
								resolve(xhr)
							} else {
								this.$toast.add({
									severity: 'error',
									summary: 'Error',
									detail: 'Update failed',
									life: 3000,
								})
								const error = new Error(
									`Request failed with status code ${xhr.status}`,
								)
								error.response = { status: xhr.status }
								reject(error)
							}
						}
					}

					xhr.open('POST', this.uploadUrl, true)

					const csrfToken = document
						.querySelector('meta[name="csrf-token"]')
						?.getAttribute('content')
					if (csrfToken) {
						xhr.setRequestHeader('X-CSRF-TOKEN', csrfToken)
					}

					xhr.send(formData)
				})
			} catch (error) {
				this.uploading = false
				throw error
			}
		},

		onSelect(e) {
			if (e.files.length > 0) {
				this.$emit('file-added')
			}
		},
	},
}
</script>

<style>
.core-file-upload {
	& .p-fileupload.p-fileupload-basic.p-component {
		& button {
			width: 100%;
			height: 51px;
			font-size: 1.6rem;
			background: var(--color-neutral-600);
			border: 1px solid var(--color-neutral-600);

			&:hover {
				background: var(--color-neutral-500);
			}
		}
	}

	& .p-fileupload.p-fileupload-basic.p-component {
		& button[data-p-disabled='true'] {
			background: var(--color-neutral-400);
			border: 1px solid var(--color-neutral-400);
			color: var(--color-secondary-500);
			cursor: not-allowed;

			&:hover {
				background: var(--color-neutral-400);
			}
		}
	}

	& button.core-file-upload__download-button {
		width: 69%;
		height: 51px;
		font-size: 1.6rem;
		background: var(--color-primary-300);
		border: 1px solid var(--color-primary-300);

		&:hover {
			background: var(--color-primary-200);
		}
	}

	& button.core-file-upload__delete-button {
		width: 30%;
		height: 51px;
		font-size: 1.6rem;
		background: var(--color-red-200);
		border: 1px solid var(--color-red-200);

		&:hover {
			background: var(--color-red-100);
		}
	}
}
span.p-button-icon.p-button-icon-left.pi {
	font-size: 1.4rem;
}
</style>
